/**
 * WebPreviousOrderPageViewModel - Web Platform Adapter
 *
 * Web-specific adapter for PreviousOrderPageViewModel.
 * Wires the shared VM with web platform services, Zustand stores,
 * and navigates to /reorder/:orderId on re-order.
 */

import { useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createPreviousOrderPageViewModel, createWebPlatformServices } from '@restaurant/viewmodels'
import { useOrderStore, useRestaurantStore } from '@restaurant/core'

/**
 * Web adapter for PreviousOrderPageViewModel.
 * Injects web platform services, order/restaurant store state,
 * and wires navigation callbacks.
 */
export const useWebPreviousOrderPageViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Order store
  const getOrderById = useOrderStore((s) => s.getOrderById)
  const isLoadingHistory = useOrderStore((s) => s.isLoadingHistory)

  // Restaurant store — for location name lookup
  const locations = useRestaurantStore((s) => s.locations)

  // Memoised platform services
  const platformServices = useMemo(
    () => createWebPlatformServices(navigate, location),
    [navigate, location],
  )

  // Location name resolver
  const getLocationName = useCallback(
    (merchantId: string): string => {
      if (!merchantId || !locations.length) return 'Restaurant'
      const loc = locations.find((l) => l.merchantId === merchantId || l.originalMerchantId === merchantId)
      return loc?.name || 'Restaurant'
    },
    [locations],
  )

  // Navigate to reorder page when re-order button is pressed
  const onReorder = useCallback(
    (orderId: string) => {
      navigate(`/reorder/${orderId}`)
    },
    [navigate],
  )

  // Create and invoke the shared hook
  const usePreviousOrderPage = useMemo(
    () =>
      createPreviousOrderPageViewModel(platformServices, {
        getOrderById,
        getLocationName,
        onReorder,
        loading: isLoadingHistory,
      }),
    [platformServices, getOrderById, getLocationName, onReorder, isLoadingHistory],
  )

  return usePreviousOrderPage()
}
